import styled, { css, keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  padding: 16px;

  @media (max-width: 600px) {
    padding: 0;
    align-items: flex-end;
  }
`;

export const ModalContainer = styled.div`
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${fadeIn} 0.3s var(--ease-out);

  @media (max-width: 600px) {
    max-width: 100%;
    max-height: 95vh;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }
`;

export const ModalHeader = styled.div`
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--border);
  text-align: center;

  h2 {
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--navy);
    margin: 0 0 6px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  p {
    font-family: var(--font-body);
    font-size: 0.875rem;
    color: var(--gray-500);
    margin: 0;
  }

  @media (max-width: 600px) {
    padding: 18px 18px 14px;

    h2 {
      font-size: 1.2rem;
    }

    p {
      font-size: 0.8rem;
    }
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 28px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: var(--gray-50);
  }
  &::-webkit-scrollbar-thumb {
    background: var(--gray-300);
    border-radius: 3px;
  }

  @media (max-width: 600px) {
    padding: 16px 18px;
  }
`;

export const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

export const ProgressTrack = styled.div`
  flex: 1;
  height: 5px;
  background: var(--gray-100);
  border-radius: 3px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: linear-gradient(90deg, var(--navy), var(--info-500));
  border-radius: 3px;
  transition: width 0.4s var(--ease-out);
`;

export const ProgressLabel = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--gray-500);
  white-space: nowrap;
`;

export const Section = styled.div<{ accepted?: boolean }>`
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--card-bg);
  margin-bottom: 12px;
  transition: border-color 0.3s, box-shadow 0.3s;
  animation: ${fadeIn} 0.3s var(--ease-out) both;

  ${({ accepted }) =>
    accepted &&
    css`
      border-color: rgba(14, 165, 114, 0.3);
      box-shadow: 0 0 0 1px rgba(14, 165, 114, 0.15);
    `}
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:hover {
    background: var(--gray-50);
  }

  @media (max-width: 600px) {
    padding: 12px 12px;
    gap: 8px;
  }
`;

export const SectionNumber = styled.div<{ accepted?: boolean }>`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: var(--font-mono);
  flex-shrink: 0;
  transition: all 0.3s;
  background: ${({ accepted }) => (accepted ? "rgba(14, 165, 114, 0.1)" : "var(--gray-100)")};
  color: ${({ accepted }) => (accepted ? "var(--success-500)" : "var(--gray-500)")};
`;

export const SectionTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--navy);

  @media (max-width: 600px) {
    font-size: 0.8rem;
  }
`;

export const Chevron = styled.svg<{ open?: boolean }>`
  width: 16px;
  height: 16px;
  color: var(--gray-400);
  flex-shrink: 0;
  transition: transform 0.3s;
  transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0)")};
`;

export const SectionBody = styled.div<{ open?: boolean }>`
  max-height: ${({ open }) => (open ? "2000px" : "0")};
  overflow: hidden;
  transition: max-height 0.4s var(--ease-out);
`;

export const SectionContent = styled.div`
  padding: 0 16px 16px;
  border-top: 1px solid var(--border);

  p {
    font-family: var(--font-body);
    font-size: 0.8125rem;
    color: var(--gray-500);
    line-height: 1.75;
    margin-top: 12px;
  }

  p strong {
    color: var(--navy);
  }

  @media (max-width: 600px) {
    padding: 0 12px 12px;

    p {
      font-size: 0.75rem;
      line-height: 1.65;
    }
  }
`;

export const NoteBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  margin-top: 12px;
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: var(--warning-600);
  line-height: 1.6;
`;

export const AcceptBox = styled.div<{ checked?: boolean }>`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  transition: all 0.25s;
  border: 1px solid var(--border);
  background: var(--gray-50);

  &:hover {
    border-color: var(--navy);
    background: rgba(26, 34, 82, 0.04);
  }

  ${({ checked }) =>
    checked &&
    css`
      border-color: rgba(14, 165, 114, 0.3);
      background: rgba(14, 165, 114, 0.06);

      &:hover {
        border-color: rgba(14, 165, 114, 0.4);
        background: rgba(14, 165, 114, 0.08);
      }
    `}
`;

export const CustomCheck = styled.div<{ checked?: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ checked }) => (checked ? "var(--success-500)" : "var(--gray-300)")};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.25s;
  background: ${({ checked }) => (checked ? "var(--success-500)" : "transparent")};

  svg {
    opacity: ${({ checked }) => (checked ? 1 : 0)};
    transform: ${({ checked }) => (checked ? "scale(1)" : "scale(0.5)")};
    transition: all 0.2s;
  }
`;

export const AcceptLabel = styled.span<{ checked?: boolean }>`
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ checked }) => (checked ? "var(--success-500)" : "var(--navy)")};
  transition: color 0.25s;
`;

export const ModalFooter = styled.div`
  padding: 20px 28px;
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  @media (max-width: 600px) {
    padding: 16px 18px;
    gap: 12px;
  }
`;

export const CounterDisplay = styled.div`
  font-family: var(--font-mono);
  font-size: 1.5rem;
  font-weight: 700;

  .done {
    color: var(--success-500);
  }
  .total {
    color: var(--gray-400);
  }
`;

export const ConfirmButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 32px;
  border-radius: var(--radius-sm);
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: 700;
  border: none;
  transition: all 0.3s;

  ${({ active }) =>
    active
      ? css`
          background: var(--navy);
          color: #fff;
          cursor: pointer;
          box-shadow: var(--shadow-navy);

          &:hover {
            background: var(--navy-light);
            transform: translateY(-1px);
            box-shadow: 0 6px 20px rgba(26, 34, 82, 0.4);
          }

          &:active {
            transform: translateY(0);
          }
        `
      : css`
          background: var(--gray-200);
          color: var(--gray-400);
          cursor: not-allowed;
        `}
`;

export const CancelButton = styled.button`
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--gray-500);
  cursor: pointer;
  padding: 8px 16px;
  transition: color 0.2s;

  &:hover {
    color: var(--red);
  }
`;
